import './translations/i18n';
import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';

import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { LicenseInfo } from '@mui/x-license';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import posthog from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import { createRoot } from 'react-dom/client';

import { API_BASE_URL } from '~constants/consts';
import ThemeModeProvider from '~contexts/ThemeMode/ThemeModeProvider';
import { maskNetworkRequest } from '~hooks/useAnalytics/maskNetworkRequest';
import { InternalErrorPage } from '~pages/Static/InternalErrorPage';
import { queryClient } from '~queries/queryClient';
import { Router } from '~router/Router';
import { isMobileBrowser } from '~utils/isMobileBrowser';
import { isProductionEnv } from '~utils/isProductionEnv';
import { sentryBeforeSend } from '~utils/sentry/sentry-reporting-utils';

dayjs.extend(utc);
dayjs.extend(timezone);
// isBetween is consumed by DriverSchedulerStore date-range filtering; register it
// globally here so it never depends on another component having rendered first.
dayjs.extend(isBetween);
dayjs.extend(relativeTime);

const posthogToken = import.meta.env.TREAD__POSTHOG_PROJECT_TOKEN;
// Skip init when the token isn't configured (posthog-js logs console errors
// otherwise). AnalyticsController no-ops whenever init is skipped. Local-dev
// and e2e traffic is not blocked here — it carries environment=development
// and is excluded via dashboard filters / PostHog internal-user filtering.
// (Don't gate on navigator.webdriver: developers' CDP-managed Chromes set it.)
if (posthogToken) {
  posthog.init(posthogToken, {
    api_host: import.meta.env.TREAD__POSTHOG_HOST,
    // Pins posthog-js defaults to their 2026-01-30 release snapshot.
    defaults: '2026-01-30',
    // Curated events only (AnalyticsController): autocaptured DOM clicks in
    // this icon-button-heavy MUI UI land as meaningless "clicked svg" rows
    // and burn event volume.
    autocapture: false,
    rageclick: false,
    // Session replay for the FullStory bake-off (TRE-15574). The master
    // enable and the ~10% sample live in the PostHog project settings, not
    // here — this block only configures what a recorded session captures.
    session_recording: {
      // Mask every input value AND all non-input text (maskTextSelector '*')
      // so no PII — names, emails, financials rendered on the page — lands in
      // a recording. Privacy-first for real-user recording (TRE-15863); the
      // repro path is reconstructed from api_error + events, not the DOM text.
      maskAllInputs: true,
      maskTextSelector: '*',
      // Capture request headers + bodies so the failing API call in a repro
      // (e.g. DELETE /v1/tickets/{id}/image -> 404) is visible with its error
      // payload — the richest transcript for the bake-off.
      // maskCapturedNetworkRequestFn scrubs PII/secrets from headers and
      // bodies in the browser before anything is sent.
      recordHeaders: true,
      recordBody: true,
      maskCapturedNetworkRequestFn: maskNetworkRequest,
    },
    // Required alongside session_recording for the network requests above to
    // appear on the replay timeline with timing.
    capture_performance: { network_timing: true },
  });
  posthog.register({
    environment: import.meta.env.TREAD__ENV,
    git_sha: import.meta.env.TREAD__GIT_SHA,
  });
}

const key = import.meta.env.TREAD__MUI_LICENCE_KEY;
LicenseInfo.setLicenseKey(key);

// Falls back to the raw value so a bad env scopes reporting to nothing, never to every host.
const apiOrigin = (() => {
  try {
    return new URL(API_BASE_URL).origin;
  } catch {
    return API_BASE_URL;
  }
})();

if (['production', 'staging', 'sandbox'].includes(import.meta.env.TREAD__ENV)) {
  Sentry.init({
    dsn: 'https://97b6c9728e794b3686f05c61d768f639@o76845.ingest.sentry.io/256719',
    release: import.meta.env.TREAD__GIT_SHA,
    environment: import.meta.env.TREAD__ENV,
    // This option is required for capturing headers and cookies.
    sendDefaultPii: true,
    integrations: function (integrations) {
      // disable the BrowserSession integration
      return integrations.filter(function (integration) {
        return integration.name !== 'BrowserSession';
      });
    },
    beforeSend: sentryBeforeSend,
  });

  // explicitly add the httpClientIntegration
  Sentry.addIntegration(
    Sentry.httpClientIntegration({
      // Configure sentry to ignore the following response statuses:
      //
      // 401 - Unauthorized
      // 403 - Forbidden
      // 422 - Unprocessable Entity
      //
      // Anything else in the 400-499 range and 500-599 range will be reported
      failedRequestStatusCodes: [400, 402, [404, 421], [423, 499], [500, 599]],
      // Our API only; the default `[/.*/]` reports third-party outages as our errors.
      failedRequestTargets: [apiOrigin],
    }),
  );
}

if ('serviceWorker' in navigator && !isMobileBrowser()) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/firebase-messaging-sw.js')
      .then((registration) => {
        console.log('Service Worker registered with scope:', registration.scope);
      })
      .catch((error) => {
        console.error('Service Worker registration failed:', error);
      });
  });
}

/**
 * Embeds a script by creating a script element and appending it to the document head.
 * @param publicPath - The public folder path of the script.
 */
const embedScript = (publicPath: string) => {
  const scriptEl = document.createElement('script');
  scriptEl.type = 'text/javascript';
  scriptEl.src = publicPath;
  document.head.appendChild(scriptEl);
};

// Only include following 3rd party scripts on production environment
if (isProductionEnv()) {
  // This code block initializes and configures the Intercom chat widget on the webpage.
  embedScript('/intercom.js');
}

const container = document.getElementById('root')!;
const root = createRoot(container);

root.render(
  <Sentry.ErrorBoundary fallback={<InternalErrorPage />}>
    <PostHogProvider client={posthog}>
      <ThemeModeProvider>
        <CssBaseline />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <QueryClientProvider client={queryClient}>
            <Router />
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeModeProvider>
    </PostHogProvider>
  </Sentry.ErrorBoundary>,
);
